// Dashboards that follow their figures.
//
// The server announces `dashboards:changed { scope }` on `/rt/dashboards`
// when a scope's figures move — a sale, a refund, an order moving on, stock,
// views (aldoo-na/store-dashboards/lib/live.js). The notice carries no
// figures: the dashboard queries again through its own HTTP action.
//
//   useLive(scope, onChange)
//
// A caller the server does not let read the scope is quietly not subscribed;
// the dashboard then behaves as before, figures as of when it was opened.
import { useEffect, useRef } from "react"
import { useRealtime } from "aldoo-ra/Realtime/hooks"

// several dashboards of one scope may be open on one socket: the room is
// asked for once, and left when the last of them goes
const watchers = new Map()
const key = (socket, scope) => `${socket.id || "socket"}:${scope}`

const join = (socket, scope) => {
  const id = key(socket, scope)
  const count = watchers.get(id) || 0
  watchers.set(id, count + 1)
  if (count === 0) socket.emit("dashboards:watch", { scope })
}

const leave = (socket, scope) => {
  const id = key(socket, scope)
  const count = watchers.get(id) || 0
  if (count <= 1) {
    watchers.delete(id)
    socket.emit("dashboards:unwatch", { scope })
    return
  }
  watchers.set(id, count - 1)
}

export default function useLive(scope, onChange) {
  const { socket, connected } = useRealtime(scope ? "dashboards" : null)
  // the handler changes on every render of the caller; the subscription must not
  const handler = useRef(onChange)
  useEffect(() => {
    handler.current = onChange
  }, [onChange])

  useEffect(() => {
    if (!socket || !connected || !scope) return
    join(socket, scope)
    const listener = (notice) => notice?.scope === String(scope) && handler.current?.(notice)
    socket.on("dashboards:changed", listener)
    return () => {
      socket.off("dashboards:changed", listener)
      leave(socket, scope)
    }
  }, [socket, connected, scope])

  return { connected }
}
